import warningIcon from "$assets/icons/warning-icon.svg?url";
import { Button } from "$components/button/button";
import { ConditionalDisplay } from "$components/conditional-display";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import { makeStyles } from "$components/mui-styles";
import ValidationContext from "$routes/_lib/validation/validation-context";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Grid, Typography } from "@material-ui/core";
import { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import useCurrencyFormatter from "../../../hooks/useCurrencyFormatter";
import { TRANSACTION_TOTAL } from "../components/formConstants";
import { TotalError } from "../components/TotalError";
import { Transactions, useBaseTransaction } from "../components/Transactions";
import { Waive } from "../components/Waive";
import { useTakePayment } from "../hooks/useTakePayment";
import { useTransactionsSchema } from "./validationSchema";

const useStyles = makeStyles((theme) => ({
  modal: {
    borderRadius: theme.spacing(3),
    overflow: "scroll",
  },
  header: {
    display: "block",
  },
  totalAndWaivedWrapper: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
  },
  totalAmountContainer: {
    marginTop: theme.spacing(3),
  },
  totalAmountTitle: {
    fontSize: "12px",
  },
  totalAmount: {
    marginTop: "4px",
    fontSize: "24px",
    fontWeight: 700,
  },
  upperDivider: {
    marginTop: "15px",
  },
  errorTotal: {
    marginBottom: -theme.spacing(2),
    position: "relative",
  },
  warningIcon: {
    marginRight: theme.spacing(2),
  },
  paymentButton: {
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      width: "100%",
    },
  },
  exemptButton: {
    marginRight: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(2),
      width: "100%",
      marginRight: 0,
    },
  },
  footer: {
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexFlow: "column-reverse",
      justifycontent: "center",
      aligncontent: "flex-start",
      width: "100%",
      alignItems: "center",
    },
  },
}));

export const TakePaymentModal: FC<{
  open: boolean;
  onClose: () => void;
  invoiceId: string;
  currency: string;
  totalPayableInPence: number;
  waiveable: boolean;
  exemptable: boolean;
  openExempt: () => void;
}> = ({
  open,
  onClose,
  invoiceId,
  currency,
  totalPayableInPence,
  waiveable,
  exemptable,
  openExempt,
}) => {
  const classes = useStyles();
  const { currencyFormatter } = useCurrencyFormatter();
  const baseTransaction = useBaseTransaction();
  const { takePayment } = useTakePayment(invoiceId);
  const [busy, setBusy] = useState(false);

  const initialValues = useMemo(
    () => ({
      transactions: [
        baseTransaction({
          amountInPence: totalPayableInPence,
          currency,
        }),
      ],
      [TRANSACTION_TOTAL]: undefined,
    }),
    [baseTransaction, currency, totalPayableInPence],
  );

  const validationSchema = useTransactionsSchema({
    totalPayableInPence,
    minValue: 0,
  });

  const formMethods = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onTouched",

    defaultValues: initialValues,
  });

  const handleSubmit = async () => {
    setBusy(true);
    try {
      await takePayment({
        transactions: formMethods.getValues().transactions,
        currency,
      });
    } finally {
      onClose();
    }
  };

  return (
    <DialogContainer
      open={open}
      className={classes.modal}
      data-testid="invoicing.takePaymentModal"
    >
      <ValidationContext.Provider value={{ validationSchema }}>
        <FormProvider {...formMethods}>
          <form
            name="take-payment-form"
            onSubmit={formMethods.handleSubmit(handleSubmit)}
          >
            <DialogHeader title="" className={classes.header}>
              <Box display="flex" alignItems="center">
                <img
                  src={warningIcon}
                  alt="warning"
                  className={classes.warningIcon}
                />
                <Typography variant="h5">
                  <FormattedMessage
                    id="invoicing.takePayment.title"
                    defaultMessage="Take Payment"
                  />
                </Typography>
              </Box>
              <div className={classes.totalAndWaivedWrapper}>
                <Box className={classes.totalAmountContainer}>
                  <span className={classes.totalAmountTitle}>
                    <FormattedMessage
                      id="invoicing.takePayment.totalAmountLabel"
                      defaultMessage="Total amount ({currency})"
                      values={{
                        currency,
                      }}
                    />
                  </span>
                  <div className={classes.totalAmount}>
                    {currencyFormatter(totalPayableInPence)}
                  </div>
                </Box>
                <ConditionalDisplay condition={waiveable}>
                  <Box>
                    <Waive
                      currency={currency}
                      totalPayableInPence={totalPayableInPence}
                    />
                  </Box>
                </ConditionalDisplay>
              </div>
            </DialogHeader>
            <Divider className={classes.upperDivider} />
            <DialogBody>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Transactions currency={currency} />
                </Grid>
                <Grid item xs={12} className={classes.errorTotal}>
                  <TotalError totalPayableInPence={totalPayableInPence} />
                </Grid>
              </Grid>
            </DialogBody>
            <Divider />
            <DialogFooter>
              <Box className={classes.footer}>
                <Box>
                  <Button
                    className={classes.paymentButton}
                    color="secondary"
                    onClick={onClose}
                    disabled={busy}
                  >
                    <FormattedMessage
                      id="invoicing.takePayment.cancel"
                      defaultMessage="Cancel"
                    />
                  </Button>
                </Box>
                <ConditionalDisplay condition={exemptable}>
                  <Button
                    onClick={openExempt}
                    color="secondary"
                    data-testid="invoicing.takePayment.exemptButton"
                    className={classes.exemptButton}
                    disabled={busy}
                  >
                    <FormattedMessage
                      data-testid="invoicing.takePayment.exempt"
                      id="invoicing.takePayment.exempt"
                      defaultMessage="Exempt"
                    />
                  </Button>
                </ConditionalDisplay>
                <Button
                  className={classes.paymentButton}
                  type="submit"
                  color="primary"
                  data-testid="invoicing.takePayment.confirmButton"
                  loading={busy}
                  disabled={
                    formMethods.formState.isSubmitting ||
                    !formMethods.formState.isValid
                  }
                >
                  <FormattedMessage
                    data-testid="invoices.takePayment.confirm"
                    id="invoicing.takePayment.confirm"
                    defaultMessage="Take Payment"
                  />
                </Button>
              </Box>
            </DialogFooter>
          </form>
        </FormProvider>
      </ValidationContext.Provider>
    </DialogContainer>
  );
};
